import { useMsal } from '@azure/msal-react';
import useSWR from 'swr';
import { getAccounts } from '@/services/api';
import { IntegrationSnippets } from '@/components/integration/IntegrationSnippets';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { PageHeader } from '@/components/PageHeader';
import { Plus, Wrench, Sparkles } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

export function InstallPage() {
  const { instance } = useMsal();
  const navigate = useNavigate();
  const [selectedAccountToken, setSelectedAccountToken] = useState<string | null>(null);

  const { data: accounts, error, isLoading } = useSWR(
    'accounts',
    () => getAccounts(instance)
  );

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div>
          <Skeleton className="h-8 w-32 mb-2" />
          <Skeleton className="h-4 w-64" />
        </div>
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertDescription>
          Failed to load accounts. Please try again.
        </AlertDescription>
      </Alert>
    );
  }

  const hasAccounts = accounts && accounts.length > 0;

  return (
    <div className="space-y-6">
      <PageHeader title="Install" description="Add the feedback collector to your application" />

      <Card className="border-primary/50 bg-primary/5">
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-primary" />
            <CardTitle className="text-base">New: Component Builder</CardTitle>
          </div>
          <CardDescription>
            Visually configure your OC component with live preview. Customise labels, tooltips, sizing, and more - then copy the generated snippet.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button onClick={() => navigate('/builder')}>
            <Wrench className="mr-2 h-4 w-4" />
            Open Builder
          </Button>
        </CardContent>
      </Card>

      <div className="flex items-center gap-4">
        <Label htmlFor="account-select" className="text-sm font-medium">
          Account:
        </Label>
        <Select
          value={selectedAccountToken || ''}
          onValueChange={(value) => setSelectedAccountToken(value || null)}
        >
          <SelectTrigger id="account-select" className="w-[300px]">
            <SelectValue placeholder="Select an account" />
          </SelectTrigger>
          <SelectContent>
            {hasAccounts ? (
              accounts.map((account) => (
                <SelectItem key={account.accountToken} value={account.accountToken}>
                  {account.name} ({account.accountToken.slice(0, 8)}...)
                </SelectItem>
              ))
            ) : (
              <SelectItem value="" disabled>
                No accounts available
              </SelectItem>
            )}
          </SelectContent>
        </Select>
      </div>

      <IntegrationSnippets accountToken={selectedAccountToken} />

      {!hasAccounts && (
        <Card className="border-dashed">
          <CardHeader className="pb-3">
            <CardTitle className="text-base">No accounts yet?</CardTitle>
            <CardDescription>
              Create an account to get your integration snippet with a real token.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={() => navigate('/account')}>
              <Plus className="mr-2 h-4 w-4" />
              Create Account
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
